import { PaperclipIcon } from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Bubble, BubbleContent, BubbleGroup } from "@/components/ui/bubble"
import { Marker, MarkerContent } from "@/components/ui/marker"
import { Message, MessageAvatar, MessageContent, MessageHeader } from "@/components/ui/message"
import { Skeleton } from "@/components/ui/skeleton"
import { ATTACHMENT_ABOUT_KEY } from "@/features/messages/attachment-copy"
import { initialsOf, istTimeOfInstant } from "@/features/messages/message-time"
import { istDayOfInstant, useFormatDay } from "@/lib/dates"
import type { MessageResponse } from "@/lib/endpoints"

/**
 * HOW A CONVERSATION LOOKS — the room's and a private one, from one file.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * EVERY LINE BELOW WAS LIFTED OUT OF THE ROOM'S SCREEN UNCHANGED (now `GroupRoom.tsx`), and that is
 * the point rather than a tidying note.
 *
 * The private conversation added on 2026-08-18 renders the SAME
 * `MessageResponse` rows the room does — the server returns one shape for both,
 * precisely so one renderer draws them. The alternative was a second set of
 * bubbles alongside the first, and the failure mode of that is not ugliness: it
 * is two answers to "what does an attachment the reader may not open look like",
 * two places for the day rule to be computed in UTC by mistake, and eventually a
 * private message that renders its body as markup because only one of the two
 * copies was audited. A conversation is a conversation.
 *
 * WHAT IS *NOT* HERE, DELIBERATELY: anything that differs between the two.
 * Where the messages come from, who may send, where the watermark is posted and
 * what the empty state says are all the SCREEN's business — these components are
 * handed rows and told which side is the reader's.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * EVERY MESSAGE IS RENDERED AS TEXT. NEVER AS HTML, NEVER AS MARKDOWN. The body
 * is stored exactly as typed — "cut the plate <100mm" survives the API unchanged,
 * deliberately — which is only safe because it is rendered through
 * `{message.body}`, which React escapes by construction. There is no
 * `dangerouslySetInnerHTML` in this feature and there must never be one;
 * `whitespace-pre-wrap` is what makes a two-line message read as two lines, and
 * it is a CSS property, not a parser.
 */

/**
 * A run of consecutive messages from ONE person, on ONE day.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * GROUPING IS PRESENTATION AND NOTHING ELSE. Every message is still rendered,
 * still exactly once, still in the server's order; a group only decides how
 * often the avatar and the author's name are repeated. Nothing here filters,
 * merges or reorders anything, and `id` remains the React key of each bubble, so
 * "what is on screen" is unchanged from the hand-rolled version.
 *
 * A DAY BOUNDARY BREAKS A GROUP even when the same person is still talking —
 * otherwise the day rule would have to be drawn INSIDE a group, between two
 * bubbles that share one header, and the header would then name a person for
 * messages sitting on both sides of a date.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export interface MessageRun {
  /** Stable across renders: the id of the first message in the run. */
  key: number
  authorUserId: number
  authorName: string
  /** The reader's own run — decided from the session USER ID, never a role. */
  mine: boolean
  /** The IST calendar day (`YYYY-MM-DD`), or null if the instant was unreadable. */
  day: string | null
  messages: MessageResponse[]
}

export function groupMessages(
  messages: readonly MessageResponse[],
  currentUserId: number | null,
): MessageRun[] {
  const runs: MessageRun[] = []

  for (const message of messages) {
    const day = istDayOfInstant(message.createdAt)
    const open = runs[runs.length - 1]

    if (open !== undefined && open.authorUserId === message.authorUserId && open.day === day) {
      open.messages.push(message)
      continue
    }

    runs.push({
      key: message.id,
      authorUserId: message.authorUserId,
      authorName: message.authorName,
      mine: currentUserId !== null && message.authorUserId === currentUserId,
      day,
      messages: [message],
    })
  }

  return runs
}

/**
 * "17 Aug 2026" between one day's messages and the next — a `Marker`, which is
 * the primitive for exactly this: a line ABOUT the conversation rather than a
 * line IN it. `variant="separator"` draws the two rules either side, so no screen
 * hand-builds them out of two `bg-border` spans.
 *
 * THE DAY WAS COMPUTED WITH `istDayOfInstant` BEFORE IT GOT HERE, and
 * `formatDay` only ever formats an already-IST calendar day. `createdAt` is an
 * ISO instant, and slicing it to ten characters takes the UTC day and prints it
 * as an IST one, so anything written between 00:00 and 05:30 IST would report
 * yesterday. The owner works late.
 */
export function DaySeparator({
  day,
  previousDay,
}: {
  day: string | null
  previousDay: string | null
}): React.JSX.Element | null {
  const formatDay = useFormatDay()

  if (day === null || day === previousDay) return null

  return (
    <Marker variant="separator" className="py-1 text-xs" data-testid="day-separator">
      <MarkerContent>{formatDay(day)}</MarkerContent>
    </Marker>
  )
}

/**
 * One run of messages, drawn with the message primitives.
 *
 * `Message align="end"` IS WHAT PUTS THE READER'S OWN WORDS ON THE RIGHT — no
 * screen flips a `flex-row-reverse` class itself, and `MessageContent` and
 * `Bubble` both read that one `data-align` off the group, so the avatar, the
 * header and every bubble in the run cannot end up disagreeing about which side
 * they are on.
 *
 * IT DOES NOT KNOW WHICH CONVERSATION IT IS IN, and must not learn. `mine` is
 * already decided (by user id, never by role — §2.1); a private thread and the
 * shared room hand it the same runs and get the same bubbles, which is the
 * property the whole file exists for.
 */
export function ConversationGroup({ group }: { group: MessageRun }): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <Message align={group.mine ? "end" : "start"} data-testid="message-group">
      {/*
        * The avatar carries INITIALS, not a photograph — there are no avatars in
        * this system and inventing an upload for three people would be a feature
        * nobody asked for. `aria-hidden`, because the author's name is already
        * printed beside the message and a screen reader announcing "SB" before it
        * is noise.
        */}
      <MessageAvatar aria-hidden="true" className="bg-transparent">
        <Avatar size="sm">
          <AvatarFallback>{initialsOf(group.authorName)}</AvatarFallback>
        </Avatar>
      </MessageAvatar>

      <MessageContent>
        {/*
          * "You" rather than the reader's own name, decided by comparing the
          * author's USER ID to the session's — never by role (§2.1). Nothing in
          * this component knows what an OWNER is.
          *
          * ONCE PER RUN rather than once per message: three messages typed in a
          * row are one person speaking, and repeating "Shubham 14:02 / Shubham
          * 14:02 / Shubham 14:03" above each of them is noise that pushes the
          * words themselves apart. The TIME stays per message, inside the
          * bubble, because that is the part that differs.
          */}
        <MessageHeader>{group.mine ? t("chat.you") : group.authorName}</MessageHeader>

        <BubbleGroup>
          {group.messages.map((message) => (
            <Bubble
              key={message.id}
              variant={group.mine ? "default" : "muted"}
              align={group.mine ? "end" : "start"}
              data-testid="message-row"
            >
              <BubbleContent>
                {/*
                  * RENDERED AS TEXT. `{message.body}` is escaped by React, which
                  * is what makes storing the body verbatim safe — see the file
                  * header. `whitespace-pre-wrap` keeps the line breaks somebody
                  * typed; it is a CSS property, not a parser.
                  */}
                <p className="break-words whitespace-pre-wrap">{message.body}</p>

                {message.attachment === null ? null : (
                  <MessageAttachment attachment={message.attachment} mine={group.mine} />
                )}

                <span
                  className="mt-1 block text-right text-[11px] tabular-nums opacity-70"
                  data-testid="message-time"
                >
                  {istTimeOfInstant(message.createdAt)}
                </span>
              </BubbleContent>
            </Bubble>
          ))}
        </BubbleGroup>
      </MessageContent>
    </Message>
  )
}

/**
 * The document a message is about.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * A LINK WHEN THE READER CAN OPEN IT, PLAIN TEXT WHEN THEY CANNOT — AND THE
 * SERVER DECIDES WHICH.
 *
 * `link` is null when the reader lacks the `*.view` capability for that document
 * kind, resolved server-side through the same `can()` this client uses (§2.1).
 * Rendering an anchor anyway would promise a screen the route guard bounces them
 * off, landing them on the home screen with no explanation — which reads as the
 * app ignoring the tap. The caption still shows either way: only the shortcut is
 * withheld, never the sentence somebody wrote.
 *
 * THE SAME RULE HOLDS IN A PRIVATE THREAD, and it is the server that keeps it
 * there — `link` is resolved per reader on both routes, so nothing about this
 * component changes when the conversation is a private one.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function MessageAttachment({
  attachment,
  mine,
}: {
  attachment: NonNullable<MessageResponse["attachment"]>
  mine: boolean
}): React.JSX.Element {
  const { t } = useTranslation()
  const caption = t(ATTACHMENT_ABOUT_KEY[attachment.type], { number: attachment.label })

  const inner = (
    <>
      <PaperclipIcon className="size-3.5 shrink-0" aria-hidden="true" />
      <span className="truncate">{caption}</span>
    </>
  )

  if (attachment.link === null) {
    return (
      <span
        className={
          mine
            ? "border-primary-foreground/30 mt-2 flex items-center gap-1.5 border-t pt-2 text-xs"
            : "border-border mt-2 flex items-center gap-1.5 border-t pt-2 text-xs"
        }
        data-testid="message-attachment"
      >
        {inner}
      </span>
    )
  }

  return (
    <Link
      to={attachment.link}
      className={
        mine
          ? "border-primary-foreground/30 mt-2 flex items-center gap-1.5 border-t pt-2 text-xs underline underline-offset-2"
          : "border-border mt-2 flex items-center gap-1.5 border-t pt-2 text-xs underline underline-offset-2"
      }
      data-testid="message-attachment"
    >
      {inner}
    </Link>
  )
}

export function ConversationSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      {[0, 1, 2].map((n) => (
        <div key={n} className="flex items-end gap-2">
          <Skeleton className="size-6 shrink-0 rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-8 w-full max-w-xs" />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * The transcript itself: day rules and runs, in the server's order.
 *
 * ONE COMPONENT SO THE TWO SCREENS CANNOT DRIFT — and it takes RUNS rather than
 * messages, because who the reader is (`mine`) is a fact about the session that
 * both screens compute identically with {@link groupMessages} and neither should
 * be able to compute differently.
 */
export function ConversationRuns({ groups }: { groups: readonly MessageRun[] }): React.JSX.Element {
  return (
    <>
      {groups.map((group, index) => (
        <React.Fragment key={group.key}>
          <DaySeparator
            day={group.day}
            previousDay={index === 0 ? null : (groups[index - 1]?.day ?? null)}
          />
          <ConversationGroup group={group} />
        </React.Fragment>
      ))}
    </>
  )
}
